import { z } from "zod";
import { SORT_KEYS } from "./sort.js";

const stringArray = z
  .union([z.string().min(1), z.array(z.string().min(1))])
  .transform((v) => (Array.isArray(v) ? v : [v]))
  .optional();

export const shopSearchSchema = z.object({
  q: z.string().trim().min(1).max(200).optional(),
  category: stringArray,
  grade: stringArray,
  curriculum: stringArray,
  subject: stringArray,
  homeschool: stringArray,
  publisher: stringArray,
  bundles: stringArray,
  sort: z.enum(SORT_KEYS).default("default"),
  page: z.coerce.number().int().min(1).default(1),
});

export type ShopSearch = z.infer<typeof shopSearchSchema>;

export type SetShopSearch = (next: Partial<ShopSearch>) => void;

type ShopArrayKey = {
  [K in keyof ShopSearch]: ShopSearch[K] extends string[] | undefined
    ? K
    : never;
}[keyof ShopSearch];

const ROOT_TO_PARAM = {
  "grade-level": "grade",
  curriculum: "curriculum",
  subject: "subject",
  bundles: "bundles",
  homeschool: "homeschool",
  publisher: "publisher",
} as const satisfies Record<string, ShopArrayKey>;

export type FacetRootHandle = keyof typeof ROOT_TO_PARAM;

export const FACET_ROOT_HANDLES: readonly FacetRootHandle[] = [
  "grade-level",
  "curriculum",
  "subject",
  "bundles",
  "homeschool",
  "publisher",
];

export type FacetChild = {
  handle: string;
  name: string;
  rank: number;
  count: number;
};

export type FacetRoot = {
  handle: string;
  name: string;
  children: FacetChild[];
};

export type FacetMap = { roots: FacetRoot[] };

export const FacetChildSchema = z.object({
  handle: z.string(),
  name: z.string(),
  rank: z.number().int(),
  count: z.number().int().nonnegative(),
}) satisfies z.ZodType<FacetChild>;

export const FacetRootSchema = z.object({
  handle: z.string(),
  name: z.string(),
  children: z.array(FacetChildSchema),
}) satisfies z.ZodType<FacetRoot>;

export const FacetMapSchema = z.object({
  roots: z.array(FacetRootSchema),
}) satisfies z.ZodType<FacetMap>;

export function categoryHandlesFromSearch(
  search: ShopSearch,
  roots: readonly FacetRootHandle[] = FACET_ROOT_HANDLES,
): string[] {
  const out: string[] = [...(search.category ?? [])];
  for (const handle of roots) {
    const values = search[ROOT_TO_PARAM[handle]];
    if (values) out.push(...values);
  }
  return out;
}

export function rootSelections(
  search: ShopSearch,
): Record<FacetRootHandle, string[]> {
  return {
    "grade-level": search.grade ?? [],
    curriculum: search.curriculum ?? [],
    subject: search.subject ?? [],
    bundles: search.bundles ?? [],
    homeschool: search.homeschool ?? [],
    publisher: search.publisher ?? [],
  };
}

export function patchForRoot(
  handle: FacetRootHandle,
  next: string[],
): Partial<ShopSearch> {
  const patch: Partial<ShopSearch> = { page: 1 };
  patch[ROOT_TO_PARAM[handle]] = next.length > 0 ? next : undefined;
  return patch;
}

export function clearAllPatch(): Partial<ShopSearch> {
  return {
    page: 1,
    category: undefined,
    grade: undefined,
    curriculum: undefined,
    subject: undefined,
    homeschool: undefined,
    publisher: undefined,
    bundles: undefined,
  };
}

export function activeFilterCount(
  search: ShopSearch,
  roots: readonly FacetRootHandle[] = FACET_ROOT_HANDLES,
): number {
  let total = search.category?.length ?? 0;
  for (const handle of roots) {
    total += search[ROOT_TO_PARAM[handle]]?.length ?? 0;
  }
  return total;
}
